import React, { useEffect, useState } from "react";
import API from "../services/api";

const ExpiredBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bids, setBids] = useState([]); // Integrated Bids State

  const fetchExpiredBookings = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await API.get("/expired-bookings");
      setBookings(res.data.bookings || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load expired bookings");
    } finally {
      setLoading(false);
    }
  };

  const openDetails = async (bookingId) => {
    try {
      setIsModalOpen(true);
      setSelectedBooking(null);
      setBids([]);

      const res = await API.get(`/booking/${bookingId}`);

      if (res.data.success) {
        setSelectedBooking(res.data.booking);
        setBids(res.data.bids || []); // Capture bids for the expired booking
      }
    } catch (err) {
      console.error("Detail Error:", err);
    }
  };

  useEffect(() => {
    fetchExpiredBookings();
  }, []);

  if (loading) return <div style={styles.centerScreen}><div style={styles.loader}></div><p>Loading Archive...</p></div>;
  if (error) return <div style={styles.errorState}><h3>⚠️ Sync Error</h3><p>{error}</p></div>;

  return (
    <div style={styles.page}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Expired Bookings</h2>
          <p style={styles.subtitle}>Historical data for unassigned or timed-out requests</p>
        </div>
        <button style={styles.refreshBtn} onClick={fetchExpiredBookings}>
          ↻ Refresh List
        </button>
      </div>

      {/* GRID */}
      {bookings.length === 0 ? (
        <div style={styles.emptyCard}>No expired records found.</div>
      ) : (
        <div style={styles.grid}>
          {bookings.map((b) => (
            <div key={b._id} style={styles.card} onClick={() => openDetails(b._id)}>
              <div style={styles.cardTop}>
                <span style={styles.bookingId}>#{b._id.slice(-6).toUpperCase()}</span>
                <span style={styles.expiredBadge}>{b.status}</span>
              </div>

              <div style={styles.routeContainer}>
                <div style={styles.locationRow}>
                  <div style={styles.dotLine} />
                  <div style={styles.locationText}>
                    <p style={styles.locName}>{b.pickupLocation}</p>
                    <p style={styles.locName}>{b.dropLocation}</p>
                  </div>
                </div>
              </div>

              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <span style={styles.label}>CUSTOMER</span>
                  <span style={styles.value}>{b.user?.name}</span>
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.label}>EXPIRED ON</span>
                  <span style={styles.value}>
                    {new Date(b.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div style={styles.cardFooter}>
                <span style={styles.footerNote}>
                  Finalized: {new Date(b.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span style={styles.viewLink}>View Archive →</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL */}
      {isModalOpen && (
        <div style={styles.overlay} onClick={() => setIsModalOpen(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={{ margin: 0 }}>Expired Record</h3>
                <span style={styles.modalSubId}>ID: {selectedBooking?._id || "..." }</span>
              </div>
              <button style={styles.closeBtn} onClick={() => setIsModalOpen(false)}>×</button>
            </div>

            <div style={styles.modalBody}>
              {!selectedBooking ? (
                <div style={{ textAlign: "center", padding: "20px" }}>Loading details...</div>
              ) : (
                <>
                  <section style={styles.section}>
                    <h4 style={styles.sectionTitle}>General Information</h4>
                    <div style={styles.detailRow}><span>Status</span> <span style={{color: '#b91c1c', fontWeight: '700'}}>{selectedBooking.status}</span></div>
                    <div style={styles.detailRow}><span>Vehicle Type</span> <strong>{selectedBooking.parcelDetails?.vehicleType}</strong></div>
                    <div style={styles.detailRow}><span>Pickup Time</span> <strong>{selectedBooking.pickupTime}</strong></div>
                  </section>

                  <section style={styles.section}>
                    <h4 style={styles.sectionTitle}>Bidding Summary (Final)</h4>
                    <div style={styles.bidTable}>
                      <div style={styles.bidTableHeader}>
                        <span>DRIVER</span>
                        <span style={{ textAlign: "right" }}>FINAL OFFER</span>
                      </div>
                      <div style={styles.bidList}>
                        {bids.length === 0 ? (
                          <div style={styles.noBids}>No bids were received for this booking.</div>
                        ) : (
                          bids.map((bid) => (
                            <div key={bid._id} style={styles.bidRow}>
                              <div style={styles.driverInfo}>
                                <div style={styles.avatar}>{bid.driver?.name?.charAt(0)}</div>
                                <span>{bid.driver?.name}</span>
                              </div>
                              <span style={styles.bidPrice}>₹{bid.amount}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </section>

                  <div style={styles.timestampBox}>
                    Created: {new Date(selectedBooking.createdAt).toLocaleString()} <br/>
                    Expired: {new Date(selectedBooking.updatedAt).toLocaleString()}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: { padding: "40px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif", color: "#1e293b" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" },
  title: { fontSize: "28px", fontWeight: "700", margin: 0 },
  subtitle: { fontSize: "14px", color: "#64748b" },
  
  refreshBtn: { padding: "10px 20px", background: "#fff", color: "#0f172a", border: "1px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", fontWeight: "600", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" },

  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "24px" },

  card: { background: "#fff", padding: "24px", borderRadius: "20px", border: "1px solid #e2e8f0", cursor: "pointer", transition: "all 0.2s" },

  cardTop: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
  bookingId: { fontSize: "12px", fontWeight: "700", color: "#64748b", background: "#f1f5f9", padding: "4px 10px", borderRadius: "6px" },
  expiredBadge: { background: "#fee2e2", color: "#b91c1c", padding: "4px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "800" },

  routeContainer: { marginBottom: "20px", padding: "12px", background: "#fff5f5", borderRadius: "12px", border: "1px solid #fee2e2" },
  locationRow: { display: "flex", gap: "12px" },
  dotLine: { width: "2px", background: "#fca5a5", borderRadius: "2px" },
  locationText: { display: "flex", flexDirection: "column", gap: "8px" },
  locName: { fontSize: "13px", fontWeight: "500", margin: 0 },

  infoGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "20px" },
  infoItem: { display: "flex", flexDirection: "column" },
  label: { fontSize: "10px", fontWeight: "700", color: "#94a3b8", marginBottom: "4px", textTransform: "uppercase" },
  value: { fontSize: "14px", fontWeight: "600" },

  cardFooter: { borderTop: "1px solid #f1f5f9", paddingTop: "15px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  footerNote: { fontSize: "12px", color: "#94a3b8" },
  viewLink: { fontSize: "12px", color: "#ef4444", fontWeight: "700" },

  overlay: { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.8)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", width: "480px", borderRadius: "24px", overflow: "hidden" },
  modalHeader: { padding: "24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" },
  modalSubId: { fontSize: "11px", color: "#94a3b8" },
  closeBtn: { fontSize: "28px", border: "none", background: "none", cursor: "pointer", color: "#94a3b8" },
  
  modalBody: { padding: "24px" },
  section: { marginBottom: "24px" },
  sectionTitle: { fontSize: "12px", fontWeight: "800", color: "#94a3b8", marginBottom: "12px" },
  detailRow: { display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "14px" },

  bidTable: { background: "#f8fafc", borderRadius: "16px", overflow: "hidden", border: "1px solid #e2e8f0" },
  bidTableHeader: { padding: "12px 16px", background: "#f1f5f9", display: "flex", justifyContent: "space-between", fontSize: "11px", fontWeight: "700" },
  bidList: { maxHeight: "180px", overflowY: "auto" },
  bidRow: { padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0" },
  driverInfo: { display: "flex", alignItems: "center", gap: "10px" },
  avatar: { width: "24px", height: "24px", background: "#94a3b8", color: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px" },
  bidPrice: { fontWeight: "700", color: "#64748b" },
  noBids: { padding: "24px", textAlign: "center", fontSize: "13px", color: "#94a3b8", fontStyle: "italic" },
  
  timestampBox: { marginTop: "20px", padding: "12px", background: "#f1f5f9", borderRadius: "8px", fontSize: "11px", color: "#64748b", lineHeight: "1.5" },

  centerScreen: { height: "80vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" },
  loader: { width: "30px", height: "30px", border: "3px solid #f3f3f3", borderTop: "3px solid #ef4444", borderRadius: "50%", animation: "spin 1s linear infinite", marginBottom: "12px" },
  emptyCard: { textAlign: "center", padding: "100px", background: "#fff", borderRadius: "20px", border: "2px dashed #e2e8f0", color: "#94a3b8" }
};

export default ExpiredBookings;